<template>
  <v-bottom-sheet v-model="showBottomSheet">
    <template #activator="{ isActive, props }">
      <v-btn class="filter__btn" :color="isActive || actived ? 'orange50' : 'black50'" height="40" :disabled="disabled" variant="text" v-bind="props">
        <FunnowSvgIcon v-if="iconPath" class="mr-2" max-width="24" :image-path="iconPath" />
        <span class="btn__text text__overflow wd-text-omd"> {{ buttonText || '' }}</span>
      </v-btn>
    </template>

    <v-card class="filter-menu-card-container !wd-px-0">
      <v-card-title class="pa-0">
        <v-row>
          <v-col class="py-4" cols="3">
            <v-btn
              v-if="showClearBtn && clearBtn"
              class="header-btn"
              :min-width="66"
              :height="40"
              color="orange50"
              variant="text"
              @click="doClearData(true)"
            >
              {{ $t('all_clear') }}
            </v-btn>
          </v-col>

          <v-col class="py-4" cols="6">
            <p class="dialog-title text-center mb-0">{{ title }}</p>
          </v-col>

          <v-col class="py-4 text-right" cols="3">
            <v-btn class="header-btn mr-5 rounded-circle" :min-width="24" :width="24" :height="24" variant="text" icon @click="doClosed">
              <v-icon> mdi-close </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <RadioFilter
        v-if="contentType === 'list'"
        :value="filterValue"
        :options="filterOptions"
        :key-value="keyValue"
        @update-clear-btn="updateClearBtn"
        @return-filter="setSelectedFilter"
      />

      <DatePicker
        v-if="contentType === 'date-picker'"
        :value="filterValue"
        :is-clear="clearing"
        :day-mode="dayMode"
        :options="filterOptions"
        @update-clear-btn="updateClearBtn"
        @return-filter="setSelectedFilter"
      />

      <TimePicker
        v-if="contentType === 'time-picker'"
        :value="filterValue"
        :is-clear="clearing"
        :selected-date="timePickerSelectedDate"
        @update-clear-btn="updateClearBtn"
        @return-filter="setSelectedFilter"
      />

      <v-card-actions class="!wd-py-6 !wd-px-5 wd-justify-end">
        <v-btn class="wd-w-full" height="40" color="orange50" elevation="0" variant="flat" @click="doConfirm">
          <loading v-if="filterLoading" />
          <span v-else-if="isDebounce"> {{ filterResultText }} </span>
          <span v-else> {{ $t('all_ok') }} </span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-bottom-sheet>
</template>
<script>
import filterActions from '@/composition/filter-actions'

const FunnowSvgIcon = defineAsyncComponent(() => import('@/components/icons/funnow-svg-icon'))
const DatePicker = defineAsyncComponent(() => import('@/components/page/categories/date-picker'))
const RadioFilter = defineAsyncComponent(() => import('@/components/page/categories/mobile/radio-filter'))
const Loading = defineAsyncComponent(() => import('@/components/icons/loading'))
const TimePicker = defineAsyncComponent(() => import('@/components/page/categories/time-picker'))

export default {
  name: 'FilterBottomSheet',
  components: {
    FunnowSvgIcon,
    DatePicker,
    RadioFilter,
    Loading,
    TimePicker,
  },
  props: {
    actived: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    iconPath: { type: String, default: '' },
    buttonText: { type: String, default: '' },
    title: { type: String, default: '' },
    clearBtn: { type: Boolean, default: false },
    contentType: { type: String, default: '' },
    filterValue: { type: [String, Number, Object, Array], default: '' },
    filterKey: { type: String, default: '' },
    dayMode: { type: Number, default: 1 },
    filterOptions: { type: Array, default: () => [] },
    currentFilter: { type: Object, default: () => ({}) },
    totalCount: { type: Number, default: 0 },
    isDebounce: { type: Boolean, default: false },
    filterType: { type: String, default: '' },
    keyValue: { type: Array, default: () => [] },
    isBranchCardMode: { type: Boolean, default: true },
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'confirmBtn'],
  setup(props) {
    const { showClearBtn, clearing, filterLoading, filterTotalCount, setSelectedFilter, doClearData, updateClearBtn, getConfirmFilterData } =
      filterActions(props)

    const timePickerSelectedDate = computed(() => props.currentFilter?.starttime || '')

    return {
      showClearBtn,
      clearing,
      filterLoading,
      filterTotalCount,
      timePickerSelectedDate,
      setSelectedFilter,
      doClearData,
      updateClearBtn,
      getConfirmFilterData,
    }
  },
  data() {
    return {
      showBottomSheet: this.modelValue || false,
    }
  },
  computed: {
    filterResultText() {
      return this.filterTotalCount <= 100
        ? this.$t('filter_result_exact_button', { result: this.filterTotalCount })
        : this.$t('filter_result_exceed_button', { result: 100 })
    },
  },
  watch: {
    showBottomSheet(newVal) {
      this.$emit('update:modelValue', newVal)
    },
    value(newVal) {
      this.showBottomSheet = newVal
    },
  },
  methods: {
    doConfirm() {
      const filterResponse = this.getConfirmFilterData()
      this.$emit('confirmBtn', { ...filterResponse, totalCount: this.filterTotalCount })
      this.doClosed()
    },
    doClosed() {
      this.hideBottomSheet()
    },
    hideBottomSheet() {
      this.showBottomSheet = false
    },
  },
}
</script>
<style lang="scss" scoped>
.filter-menu-card-container {
  border-radius: 0 !important;
  border-top-left-radius: 12px !important;
  border-top-right-radius: 12px !important;
}

.dialog-title,
.header-btn {
  line-height: 40px;
  font-size: 17px;
}

.btn__text {
  max-width: 110px;
  @media only screen and (max-width: 320) {
    max-width: 62px;
  }
}

.text__overflow {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-btn {
  font-weight: normal;
}
</style>
